import React, { useCallback, useEffect, useState } from 'react';
import {
  getRecentPurchasedItems,
  getSubscribedItems,
  subscribeItems,
  unsubscribeItems,
} from '../api/wishList';
import WishList from '../components/WishList';
import { useAuthState } from '../../preorder/plugins/Auth';

const toggleItem = (array = [], item) => {
  const index = array.indexOf(item);
  if (index > -1) return array.filter((i) => i !== item);
  return [...array, item];
};

const METHOD_TYPE = {
  SUBSCRIBE: 'SUBSCRIBE',
  UNSUBSCRIBE: 'UNSUBSCRIBE',
};

export default () => {
  const [
    selectedItemIdsToSubscribe,
    setSelectedItemIdsToSubscribe,
  ] = useState([]);
  const [
    selectedItemIdsToUnsubscribe,
    setSelectedItemIdsToUnsubscribe,
  ] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useAuthState();
  const [recentPurchasedItems, setRecentPurchasedItems] = useState([]);
  const [subscribedItems, setSubscribedItems] = useState([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [getRecentPurchasedItemsResult, getSubscribedItemsResult] = await Promise.all([
        getRecentPurchasedItems(token, 3),
        getSubscribedItems(token),
      ]);
      const {
        data: getRecentPurchasedItemsData,
        message: getRecentPurchasedItemsMessage,
        status: getRecentPurchasedItemsStatus,
      } = getRecentPurchasedItemsResult || {};
      const {
        data: getSubscribedItemsData,
        message: getSubscribedItemsMessage,
        status: getSubscribedItemsStatus,
      } = getSubscribedItemsResult || {};

      if (getRecentPurchasedItemsStatus !== 'success') throw new Error(getRecentPurchasedItemsMessage);
      if (getSubscribedItemsStatus !== 'success') throw new Error(getSubscribedItemsMessage);

      const { items: getRecentPurchasedItemsItems } = getRecentPurchasedItemsData || {};
      const { items: getSubscribedItemsItems } = getSubscribedItemsData || {};

      setRecentPurchasedItems(getRecentPurchasedItemsItems.filter((recentPurchasedItem) => {
        const {
          productName: recentPurchasedItemProductName,
          fridgeName: recentPurchasedItemFridgeName,
        } = recentPurchasedItem;
        return !getSubscribedItemsItems.map((subscribedItem) => {
          const {
            productName: subscribedItemProductName,
            fridgeName: subscribedItemFridgeName,
          } = subscribedItem;

          return `${subscribedItemProductName}_${subscribedItemFridgeName}`;
        }).includes(`${recentPurchasedItemProductName}_${recentPurchasedItemFridgeName}`);
      }) || []);
      setSubscribedItems(getSubscribedItemsItems || []);
    } catch (e) {
      console.log(e.message);
    }

    setIsLoading(false);
  }, [setRecentPurchasedItems, setSubscribedItems, setIsLoading, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onItemClick = useCallback((methodName) => (e) => {
    const { value } = e.currentTarget;
    const methodMap = {
      [METHOD_TYPE.SUBSCRIBE]: setSelectedItemIdsToSubscribe,
      [METHOD_TYPE.UNSUBSCRIBE]: setSelectedItemIdsToUnsubscribe,
    };
    const arrMap = {
      [METHOD_TYPE.SUBSCRIBE]: selectedItemIdsToSubscribe,
      [METHOD_TYPE.UNSUBSCRIBE]: selectedItemIdsToUnsubscribe,
    };

    methodMap[methodName](toggleItem(arrMap[methodName], value));
  }, [
    selectedItemIdsToSubscribe,
    selectedItemIdsToUnsubscribe,
    setSelectedItemIdsToSubscribe,
    setSelectedItemIdsToUnsubscribe,
  ]);

  const onButtonClick = useCallback((methodName) => async () => {
    const methodMap = {
      [METHOD_TYPE.SUBSCRIBE]: subscribeItems,
      [METHOD_TYPE.UNSUBSCRIBE]: unsubscribeItems,
    };
    const arrMap = {
      [METHOD_TYPE.SUBSCRIBE]: selectedItemIdsToSubscribe,
      [METHOD_TYPE.UNSUBSCRIBE]: selectedItemIdsToUnsubscribe,
    };
    const selectMethodMap = {
      [METHOD_TYPE.SUBSCRIBE]: setSelectedItemIdsToSubscribe,
      [METHOD_TYPE.UNSUBSCRIBE]: setSelectedItemIdsToUnsubscribe,
    };

    setIsLoading(true);

    try {
      const result = await methodMap[methodName](token, arrMap[methodName]);
      const { status, message } = result || {};

      if (status !== 'success') throw new Error(message);

      await loadData();

      selectMethodMap[methodName]([]);
    } catch (error) {
      console.log(error.message);
    }

    setIsLoading(false);
  }, [selectedItemIdsToSubscribe, token, selectedItemIdsToUnsubscribe, loadData]);

  return (
    <WishList
      isLoading={isLoading}
      onSubscribeButtonClick={onButtonClick(METHOD_TYPE.SUBSCRIBE)}
      onSubscribeItemClick={onItemClick(METHOD_TYPE.SUBSCRIBE)}
      onUnsubscribeButtonClick={onButtonClick(METHOD_TYPE.UNSUBSCRIBE)}
      onUnsubscribeItemClick={onItemClick(METHOD_TYPE.UNSUBSCRIBE)}
      recentPurchasedItems={recentPurchasedItems}
      selectedItemIdsToSubscribe={selectedItemIdsToSubscribe}
      selectedItemIdsToUnsubscribe={selectedItemIdsToUnsubscribe}
      subscribedItems={subscribedItems}
    />
  );
};
